import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IoMdArrowDropdown, IoMdCloseCircleOutline } from 'react-icons/io';
import { CircleFlag } from 'react-circle-flags';
import { MdInfoOutline } from 'react-icons/md';
import debounce from 'lodash/debounce';
import { apiPostDataWithLangISO } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';

// Mora bit usklađeno sa transformFormData iz assets/responsive-frontend/pages/PostOrderGuests/PostOrderGuestsContent.jsx
const SEARCHABLE_FIELDS_CONFIG = {
    countryCode: { searchApiPath: 'countries/autocomplete/' },
    ownerAddressCountry: { searchApiPath: 'countries/autocomplete/' },
    bankAddressCountry: { searchApiPath: 'countries/autocomplete/' },
    birthCountry1: { searchApiPath: 'countries/autocomplete/' },
    birthCountry2: { searchApiPath: 'countries/autocomplete/' },
    birthCountry3: { searchApiPath: 'countries/autocomplete/' },
    birthCountry4: { searchApiPath: 'countries/autocomplete/' },
    birthCountry5: { searchApiPath: 'countries/autocomplete/' },
    birthCountry6: { searchApiPath: 'countries/autocomplete/' },
    birthCountry7: { searchApiPath: 'countries/autocomplete/' },
    birthCountry8: { searchApiPath: 'countries/autocomplete/' },
};

export default function SelectBoxField({
    fieldData,
    currentStateValue,
    onChangeHandler,
    controlStateData,
    isCountrySelect = false,
    disabledField = false,
}) {
    const fieldNameKey = fieldData.name;
    const searchableConfig = SEARCHABLE_FIELDS_CONFIG[fieldNameKey];
    const isSearchable = Boolean(searchableConfig);
    const searchApiPath = searchableConfig?.searchApiPath ?? '';
    const fieldDataId = fieldData.id ?? fieldData.name;
    const selectedValue = typeof currentStateValue === 'string' ? currentStateValue.trim() : currentStateValue;
    const isEmptyState = !selectedValue;
    const isFormSubmit = controlStateData && controlStateData.isSubmit;
    const isRequiredError = fieldData.isRequired && isEmptyState;
    const isAnyError = isRequiredError;

    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    // State za search
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [unrecognizedCountryLabel, setUnrecognizedCountryLabel] = useState(null);
    const [isFetchingResults, setIsFetchingResults] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const searchInputRef = useRef(null);
    const fetchAbortControllerRef = useRef(null);

    // Sinkroniziraj prikazanu vrijednost polja za pretraživanje kada se vanjska vrijednost promijeni (npr. nakon resetiranja forme)
    useEffect(() => {
        if (isSearchable) {
            // Ako postoji odabrana vrijednost, pronađi i prikaži njezin naziv, u suprotnom očisti input
            const matchedOption = (fieldData.options ?? []).find((opt) => opt.value === selectedValue);
            setSearchInputValue(matchedOption ? matchedOption.title : '');
        }
    }, [isSearchable, selectedValue, fieldData.options]);

    // Klik izvan komponente zatvara padajući izbornik
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
                if (isSearchable) {
                    setFocusedIndex(null);
                    // Ako je korisnik upisao tekst, ali nije odabrao stavku, vrati naziv trenutno odabrane vrijednosti
                    const matchedOption = (fieldData.options ?? []).find((opt) => opt.value === selectedValue);
                    setSearchInputValue(matchedOption ? matchedOption.title : '');
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isSearchable, selectedValue, fieldData.options]);

    const getErrorMessage = () => {
        if (!isFormSubmit) { return null; }
        if (isRequiredError) { return fieldData.requiredError; }
        return null;
    };

    const hasSuggestedOptions = fieldData.suggestedOptions && fieldData.suggestedOptions.length > 0;

    // Uklanjamo prvu opciju tj NULL opciju iz options kad postoje suggestedOptions.
    const filteredOptions = hasSuggestedOptions
        ? (fieldData.options ?? []).filter((_, index) => index !== 0)
        : (fieldData.options ?? []);

    const handleSelect = (value) => {
        onChangeHandler({ target: { name: fieldData.name, value } });
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChangeHandler({ target: { name: fieldData.name, value: '' } });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchSearchResults = useCallback(
        debounce((query) => {
            if (!query || query.trim().length < 1) {
                setSearchResults([]);
                setIsFetchingResults(false);
                return;
            }

            fetchAbortControllerRef.current?.abort();
            fetchAbortControllerRef.current = new AbortController();

            setIsFetchingResults(true);

            apiPostDataWithLangISO(
                searchApiPath,
                { query },
                { signal: fetchAbortControllerRef.current.signal },
            )
                .then((res) => {
                    if (res.status === 200) {
                        const results = res.data.results ?? [];
                        setSearchResults(results);
                        setUnrecognizedCountryLabel(results.length === 0 ? (res.data.unrecognizedCountryLabel ?? null) : null);
                        setFocusedIndex(results.length > 0 ? 0 : null);
                        setHasSearched(true);
                    }
                })
                .catch((err) => {
                    reportError(err);
                })
                .finally(() => {
                    setIsFetchingResults(false);
                });
        }, 300),
        [searchApiPath],
    );

    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchInputValue(query);
        setFocusedIndex(null);

        // Očisti stvarnu vrijednost forme dok korisnik upisuje tekst
        if (selectedValue) {
            onChangeHandler({ target: { name: fieldData.name, value: '' } });
        }

        if (!query.trim()) {
            fetchAbortControllerRef.current?.abort();
            setSearchResults([]);
            setUnrecognizedCountryLabel(null);
            setIsFetchingResults(false);
            setHasSearched(false);
            return;
        }

        setIsFetchingResults(true);
        fetchSearchResults(query);
    };

    const handleSearchInputFocus = () => {
        if (disabledField) return;
        setIsOpen(true);
        // Ako u polju već postoji tekst i nema trenutno odabrane vrijednosti, pokreni fetch
        if (searchInputValue && !selectedValue) {
            setIsFetchingResults(true);
            fetchSearchResults(searchInputValue);
        }
    };

    const handleSearchInputMouseDown = (e) => {
        if (disabledField) return;
        if (isOpen) {
            e.preventDefault();
            setIsOpen(false);
            setFocusedIndex(null);
            searchInputRef.current?.blur();
        }
    };

    const handleSearchSelect = (result) => {
        // Očekivani oblik rezultata: { value, title } – isti kao stavke u fieldData.options
        onChangeHandler({ target: { name: fieldData.name, value: result.value } });
        setSearchInputValue(result.title);
        setSearchResults([]);
        setHasSearched(false);
        setIsOpen(false);
        setFocusedIndex(null);
        searchInputRef.current?.blur();
    };

    const handleSearchClear = (e) => {
        e.stopPropagation();
        fetchAbortControllerRef.current?.abort();
        setSearchInputValue('');
        setSearchResults([]);
        setUnrecognizedCountryLabel(null);
        setHasSearched(false);
        setFocusedIndex(null);
        onChangeHandler({ target: { name: fieldData.name, value: '' } });
        searchInputRef.current?.focus();
    };

    // Navigacija tipkovnicom
    const handleSearchKeyDown = (e) => {
        if (!isOpen) return;
        const itemCount = searchResults.length;
        if (itemCount === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, itemCount - 1)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev === null ? itemCount - 1 : Math.max(prev - 1, 0)));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedIndex !== null && searchResults[focusedIndex]) {
                handleSearchSelect(searchResults[focusedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    // Zadane vrijednosti za suggestedOptions i filteredOptions koje se prikazuju prije prvog pretraživanja
    const defaultOptionsBlock = (
        <>
            {hasSuggestedOptions && (
                <>
                    {fieldData.suggestedOptions.map((item, index) => (
                        <button
                            type="button"
                            key={`suggested-${index}`}
                            className={`form__select--dropdownItem ${!item.value?.trim() ? 'form__select--dropdownItem-placeholder' : ''} ${selectedValue === item.value ? 'form__select--dropdownItem-active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); handleSearchSelect(item); }}
                        >
                            <span className="form__select--dropdownItemText">{item.title}</span>
                        </button>
                    ))}
                    <div className="form__select--dropdownSeparator" />
                </>
            )}
            {filteredOptions.map((item, index) => (
                <button
                    type="button"
                    key={`option-${index}`}
                    className={`form__select--dropdownItem ${!item.value?.trim() ? 'form__select--dropdownItem-placeholder' : ''} ${selectedValue === item.value ? 'form__select--dropdownItem-active' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); handleSearchSelect(item); }}
                >
                    <span className="form__select--dropdownItemText">{item.title}</span>
                </button>
            ))}
        </>
    );

    if (isSearchable) {
        const isSearchableEmptyState = !selectedValue;
        const isSearchableError = fieldData.isRequired && isSearchableEmptyState;

        return (
            <>
                <div
                    id={`${fieldDataId}_wrapper`}
                    ref={wrapperRef}
                    className={`form__input--wrapper ${isSearchableError && isFormSubmit ? 'form__input--wrapper-error' : ''} ${!isSearchableEmptyState ? 'form__input--wrapper-filled' : 'form__input--wrapper-empty'}`}
                >
                    <input
                        ref={searchInputRef}
                        id={fieldDataId}
                        name={fieldData.name}
                        type="text"
                        autoComplete="off"
                        className={`form__input form__input--select ${isCountrySelect ? 'form__input--selectCountry' : ''} ${isSearchableEmptyState ? 'form__input--emptySelect' : ''}`}
                        placeholder={fieldData.placeholderText ?? ''}
                        value={searchInputValue}
                        disabled={disabledField}
                        onChange={handleSearchInputChange}
                        onMouseDown={handleSearchInputMouseDown}
                        onFocus={handleSearchInputFocus}
                        onKeyDown={handleSearchKeyDown}
                    />
                    <label
                        className={`form__label form__label--select ${isCountrySelect ? 'form__label--selectCountry' : ''} ${!isSearchableEmptyState && 'filled__input'}`}
                        htmlFor={fieldDataId}
                    >
                        <span className="form__labelTitle">
                            {fieldData.title}{fieldData.isRequired && <span className="form__asterisk--required"> *</span>}
                        </span>
                    </label>
                    <IoMdArrowDropdown className={`form__input--arrow ${isOpen ? 'form__input--arrowOpen' : ''}`} />
                    {(searchInputValue || !isSearchableEmptyState) && !disabledField && (
                        <button
                            type="button"
                            className="form__input--clearButton form__input--clearButtonDropdown"
                            aria-label="Clear selection"
                            onClick={handleSearchClear}
                            tabIndex={-1}
                        >
                            <IoMdCloseCircleOutline className="form__input--clearIcon" />
                        </button>
                    )}
                    {isCountrySelect && (
                        <div className="form__input--countryIconContainer">
                            {selectedValue ? (
                                <CircleFlag countryCode={selectedValue} width="20" height="20" />
                            ) : (
                                <div className="form__input--countryIconDefault" />
                            )}
                        </div>
                    )}

                    {isOpen && (
                        <div className="form__select--dropdown" role="listbox">
                            <div className="form__select--dropdownInner">
                                {(!hasSearched || !searchInputValue.trim()) && !isFetchingResults && defaultOptionsBlock}

                                {isFetchingResults && defaultOptionsBlock}

                                {!isFetchingResults && hasSearched && searchInputValue.trim() && searchResults.length > 0 && searchResults.map((result, index) => (
                                    <button
                                        type="button"
                                        key={`search-result-${index}`}
                                        className={`form__select--dropdownItem ${selectedValue === result.value ? 'form__select--dropdownItem-active' : ''} ${focusedIndex === index ? 'form__select--dropdownItem-active' : ''}`}
                                        onMouseDown={(e) => { e.preventDefault(); handleSearchSelect(result); }}
                                    >
                                        <span className="form__select--dropdownItemText">{result.title}</span>
                                    </button>
                                ))}

                                {!isFetchingResults && hasSearched && searchInputValue.trim() && searchResults.length === 0 && (
                                    <>
                                        {unrecognizedCountryLabel && (
                                            <div className="form__select--dropdownItemEmptyText">
                                                <MdInfoOutline className="locationDropdown-svg locationDropdown__info-box-icon" />
                                                {unrecognizedCountryLabel}
                                            </div>
                                        )}
                                        {defaultOptionsBlock}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <span className={`form__message form__message--error ${isSearchableError && isFormSubmit ? 'form__message--visible' : ''}`}>
                    {(isSearchableError && isFormSubmit ? fieldData.requiredError : null) ?? <>&nbsp;</>}
                </span>
            </>
        );
    }

    return (
        <>
            <div
                id={`${fieldDataId}_wrapper`}
                ref={wrapperRef}
                className={`form__input--wrapper ${isAnyError && isFormSubmit ? 'form__input--wrapper-error' : ''} ${!isEmptyState ? 'form__input--wrapper-filled' : 'form__input--wrapper-empty'}`}
            >
                <select
                    id={fieldDataId}
                    name={fieldData.name}
                    value={selectedValue ?? ''}
                    onChange={onChangeHandler}
                    className={`form__input form__input--select ${isCountrySelect ? 'form__input--selectCountry' : ''} ${isEmptyState ? 'form__input--emptySelect' : ''}`}
                    disabled={disabledField}
                >
                    {hasSuggestedOptions ? (
                        <>
                            <optgroup label="">
                                {(fieldData.suggestedOptions ?? []).map((item, index) => (
                                    <option value={item.value} key={`suggested-${index}`}>
                                        {item.title || fieldData.placeholderText}
                                    </option>
                                ))}
                            </optgroup>
                            <optgroup label="">
                                {filteredOptions.map((item, index) => (
                                    <option value={item.value} key={`option-${index}`}>
                                        {item.title || fieldData.placeholderText}
                                    </option>
                                ))}
                            </optgroup>
                        </>
                    ) : (
                        (fieldData.options ?? []).map((item, index) => (
                            <option value={item.value} key={index}>
                                {item.title || fieldData.placeholderText}
                            </option>
                        ))
                    )}
                </select>
                <label
                    className={`form__label form__label--select ${isCountrySelect ? 'form__label--selectCountry' : ''} ${!isEmptyState && 'filled__input'}`}
                    htmlFor={fieldDataId}
                >
                    <span className="form__labelTitle">
                        {fieldData.title}{fieldData.isRequired && <span className="form__asterisk--required"> *</span>}
                    </span>
                </label>
                <IoMdArrowDropdown className={`form__input--arrow ${isOpen ? 'form__input--arrowOpen' : ''}`} />
                {!isEmptyState && !disabledField && (
                    <button type="button" className="form__input--clearButton form__input--clearButtonDropdown" aria-label="Clear selection" onClick={handleClear} tabIndex={-1}>
                        <IoMdCloseCircleOutline className="form__input--clearIcon" />
                    </button>
                )}
                {isCountrySelect && (
                    <div className="form__input--countryIconContainer">
                        {selectedValue ? (
                            <CircleFlag countryCode={selectedValue} width="20" height="20" />
                        ) : (
                            <div className="form__input--countryIconDefault" />
                        )}
                    </div>
                )}

                {!disabledField && (
                    <button
                        type="button"
                        className="form__select--clickOverlay d-none d-md-block"
                        onClick={() => setIsOpen((prev) => !prev)}
                        tabIndex={-1}
                        aria-label="Open dropdown"
                    />
                )}

                {isOpen && (
                    <div className="form__select--dropdown" role="listbox">
                        <div className="form__select--dropdownInner">
                            {hasSuggestedOptions && (
                                <>
                                    {fieldData.suggestedOptions.map((item, index) => (
                                        <button
                                            type="button"
                                            key={`suggested-${index}`}
                                            className={`form__select--dropdownItem ${!item.value?.trim() ? 'form__select--dropdownItem-placeholder' : ''} ${selectedValue === item.value ? 'form__select--dropdownItem-active' : ''}`}
                                            onMouseDown={(e) => { e.preventDefault(); handleSelect(item.value); }}
                                        >
                                            <span className="form__select--dropdownItemText">{item.title || fieldData.placeholderText}</span>
                                        </button>
                                    ))}
                                    <div className="form__select--dropdownSeparator" />
                                </>
                            )}
                            {filteredOptions.map((item, index) => (
                                <button
                                    type="button"
                                    key={`option-${index}`}
                                    className={`form__select--dropdownItem ${!item.value?.trim() ? 'form__select--dropdownItem-placeholder' : ''} ${selectedValue === item.value ? 'form__select--dropdownItem-active' : ''}`}
                                    onMouseDown={(e) => { e.preventDefault(); handleSelect(item.value); }}
                                >
                                    <span className="form__select--dropdownItemText">{item.title || fieldData.placeholderText}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <span className={`form__message form__message--error ${isAnyError && isFormSubmit ? 'form__message--visible' : ''}`}>
                {getErrorMessage() ?? <>&nbsp;</>}
            </span>
        </>
    );
}
